// A reminder for an action on the operator's own Google Calendar, made from the browser at
// the click as a Gmail draft is: the grant lives in this tab only, so no stored token and
// no agent can write to anyone's calendar. See docs/Y2026-Q4/design-add-to-calendar.md.
import {
  ensureGsiLoaded,
  refreshGoogleAccessToken,
  requestGoogleAccessToken,
} from "./google-auth";
import { GOOGLE_CALENDAR_EVENTS_SCOPE } from "./google-config";

const EVENTS_URL = "https://www.googleapis.com/calendar/v3/calendars/primary/events";
const SCOPES = [GOOGLE_CALENDAR_EVENTS_SCOPE];

/** 10am in the operator's own time zone unless they pick a time, for half an hour
 * (Noaa, 28 Oct 2026). */
export const REMINDER_TIME = "10:00";
const LENGTH_MINUTES = 30;

/** "10am", "2:30pm": how a reminder's ``HH:MM`` reads. */
export const formatTime = (time: string): string => {
  const [h, m] = time.split(":").map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
};

/** The wall-clock start and end, as Calendar takes them with a time zone: half an hour
 * on, into the next day when it starts after 11:30pm. */
const span = (day: string, time: string): { start: string; end: string } => {
  const [y, mo, d] = day.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const end = new Date(Date.UTC(y, mo - 1, d, h, mi + LENGTH_MINUTES));
  return {
    start: `${day}T${time}:00`,
    end: end.toISOString().slice(0, 19),
  };
};

export interface Reminder {
  /** What the event is kept against, so a second press for the same day finds it. */
  actionId: string;
  /** YYYY-MM-DD. */
  day: string;
  /** HH:MM, in the operator's time zone. */
  time: string;
  title: string;
  description: string;
}

export interface SavedReminder {
  /** The event in Google Calendar. */
  link: string;
  /** True when this action already had a reminder on this day, which is kept. */
  existing: boolean;
}

class CalendarStatusError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const failed = async (what: string, resp: Response): Promise<never> => {
  throw new CalendarStatusError(
    resp.status,
    `Google Calendar could not ${what} (${resp.status}): ${await resp.text()}`,
  );
};

const call = (token: string, url: string, init: RequestInit = {}): Promise<Response> =>
  fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });

// The event made for an action at a day and time, so a second press adds no second event.
// A reminder at another day or time is its own: one on the due date and another on the
// deadline are both kept.
const STORAGE_PREFIX = "inrange-agents.calendar-event.";

const storageKey = (r: Reminder): string =>
  `${STORAGE_PREFIX}${r.actionId}.${r.day}.${r.time}`;

const storedEventId = (r: Reminder): string | null => {
  try {
    return window.localStorage.getItem(storageKey(r));
  } catch {
    return null;
  }
};

const storeEventId = (r: Reminder, id: string): void => {
  try {
    window.localStorage.setItem(storageKey(r), id);
  } catch {
    // Unavailable storage only costs a second event on a later press.
  }
};

interface CalendarEvent {
  id: string;
  htmlLink: string;
  status?: string;
}

/** Put the reminder on the operator's primary calendar, or find the one this browser
 * already made for the action on that day. The token comes first, while the click can
 * still open Google's consent popup. */
export const addReminder = async (reminder: Reminder): Promise<SavedReminder> => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { start, end } = span(reminder.day, reminder.time);
  const run = async (token: string): Promise<SavedReminder> => {
    const known = storedEventId(reminder);
    if (known) {
      const resp = await call(token, `${EVENTS_URL}/${encodeURIComponent(known)}`);
      if (resp.ok) {
        const event = (await resp.json()) as CalendarEvent;
        if (event.status !== "cancelled") return { link: event.htmlLink, existing: true };
      } else if (resp.status !== 404 && resp.status !== 410) {
        // 404/410: deleted, or made on another Google account; make a new one.
        await failed("read the reminder already made", resp);
      }
    }
    const resp = await call(token, EVENTS_URL, {
      method: "POST",
      body: JSON.stringify({
        summary: reminder.title,
        description: reminder.description,
        start: { dateTime: start, timeZone },
        end: { dateTime: end, timeZone },
      }),
    });
    if (!resp.ok) await failed("add the reminder", resp);
    const event = (await resp.json()) as CalendarEvent;
    storeEventId(reminder, event.id);
    return { link: event.htmlLink, existing: false };
  };

  await ensureGsiLoaded();
  const token = await requestGoogleAccessToken(SCOPES);
  try {
    return await run(token);
  } catch (err) {
    if (!(err instanceof CalendarStatusError) || err.status !== 401) throw err;
    return run(await refreshGoogleAccessToken(SCOPES));
  }
};
